// Helper to ensure a value is a string (for ReactMarkdown)
function toMarkdownString(val: unknown): string {
  if (Array.isArray(val)) return val.join('');
  if (typeof val === 'string') return val;
  if (val == null) return '';
  return String(val);
}

// @ts-ignore
import { saveAs } from 'file-saver';
import React, { FormEvent, useContext, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { DefaultButton, Dialog, Stack, Dropdown, PrimaryButton } from '@fluentui/react';
import { ThumbDislike20Filled, ThumbLike20Filled, DocumentEdit24Regular, ArrowDownload20Regular, ArrowExpand20Regular, Dismiss24Regular } from '@fluentui/react-icons';
import DOMPurify from 'dompurify';
import remarkGfm from 'remark-gfm';
import supersub from 'remark-supersub';
import rehypeRaw from 'rehype-raw';
import { ChartRenderer } from './ChartRenderer';
import { ArchitectureDiagram } from '../ArchitectureDiagram';
import { AskResponse, Citation, Feedback } from '../../api';
import { authenticatedFetch } from '../../utils/authApi';
import { buildApiUrl } from '../../config/apiConfig';
import { openFile, downloadFile } from '../../utils/fileOpenUtils';
import { XSSAllowTags, XSSAllowAttributes } from '../../constants/sanatizeAllowables';
import { AppStateContext } from '../../state/AppProvider';
import { parseAnswer } from './AnswerParser';
import RealTimeCitation from './RealTimeCitation';
import ResearchCitation from './ResearchCitation';
import VisualSources from './VisualSources';
import styles from './Answer.module.css';
import CitationSourcesPanel, { UnifiedCitationSource } from './CitationSourcesPanel';
import { SourceIndicator } from '../SourceIndicator';
import { ModelIndicator } from '../ModelIndicator';
import { ThinkingProcessIndicator } from '../ThinkingProcessIndicator';
import { Dashboard } from '../Dashboard/Dashboard';
import ParagraphTooltip from './ParagraphTooltip';
import AzureIcon from '../../assets/Azure.svg';
import GoogleIcon from '../../assets/Google.svg';
import BookIcon from '../../assets/Book.svg';
import rtlDetect from 'rtl-detect';

// Regex to match paragraph and section references:
// - Paragraph refs: ¶69–94, ¶52, ¶12-15 (with or without brackets)
// - Section refs: §1.1.2, §2.3 (with or without brackets)
// Matches: ¶170–183, 【¶52】, [§1.1.2], §1.2.3, etc.
const PARAGRAPH_REF_REGEX = /(?:[【\[])?([¶§][\d.]+(?:[–\-][\d.]+)?)(?:[】\]])?/g;

// Web source citation format: 【4:2†source】or 【1:0†filename.pdf】
// Transform these into cleaner citation badges
const WEB_CITATION_REGEX = /【(\d+):(\d+)†([^】]+)】/g;

// Helper to transform web citations into clean badges
function transformWebCitations(text: string): string {
  return text.replace(WEB_CITATION_REGEX, (_match, docNum, _chunkNum, _sourceName) => {
    // Return a cleaner superscript reference with inline styling
    return `<sup style="display: inline-flex; align-items: center; justify-content: center; min-width: 16px; height: 16px; padding: 0 4px; margin: 3px !important; font-size: 10px; font-weight: 600; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border-radius: 8px; vertical-align: super; line-height: 1;">${docNum}</sup>`;
  });
}

// Helper to transform text with paragraph/section references into React elements with tooltips
function transformParagraphRefs(
  text: string, 
  citations: any[] = [], 
  isRtl: boolean = false
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  
  // Reset regex state
  PARAGRAPH_REF_REGEX.lastIndex = 0;
  
  while ((match = PARAGRAPH_REF_REGEX.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    // Add the paragraph tooltip component
    const paraRef = match[1]; // The paragraph reference (e.g., ¶170–183)
    parts.push(
      <ParagraphTooltip 
        key={`para-${key++}`}
        paragraphRef={paraRef}
        citations={citations}
        isRtl={isRtl}
      />
    );
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return parts.length > 0 ? parts : [text];
}


// Helper to export answer content to file
async function exportAnswerToFile({ content, title, format }: { content: string, title: string, format: 'word' | 'pdf' }) {
  const response = await authenticatedFetch('/document/export', {
    method: 'POST',
    body: JSON.stringify({ content, title, format })
  });
  if (!response.ok) {
    alert('Failed to export document.');
    return;
  }
  const blob = await response.blob();
  const ext = format === 'word' ? 'docx' : 'pdf';
  saveAs(blob, `${title || 'document'}.${ext}`);
}


// Props interface

export interface Props {
  answer: AskResponse;
  source?: string;
  sourceConfidence?: number;
  document_metadata?: Array<{filename: string; confidence: number; chunks_found?: number; generated_dashboard?: any; chart_data?: any; chart_specs?: any[]}>;
  onCitationClicked: (citedDocument: Citation) => void;
  onExectResultClicked: (answerId: string) => void;
  onEditDocument?: (workspaceId: string) => void;  // Callback to open document editor
  simple?: boolean;
  intent_reasoning?: {
    data_source: string;
    reason: string;
    confidence: number;
    expert_guidance?: boolean;
    system_message_type?: string;
    tools_activated?: string[];
    table_routing?: {
      method: string;
      tables_queried: string[];
      all_available_tables: string[];
      used_all_tables: boolean;
    };
  };
  showThinkingProcess?: boolean;
  isCached?: boolean;  // Whether this response came from FAQ cache
  showCacheBadge?: boolean;  // Whether to show cache badge (admin toggle)
}


// Dummy values for missing constants (replace with real logic as needed)
const SANITIZE_ANSWER = true;
const FEEDBACK_ENABLED = true;

// Dummy feedback state and handlers (replace with real logic as needed)
const FeedbackStateStub = Feedback.Neutral;
const onLikeResponseClicked = () => {};
const onDislikeResponseClicked = () => {};
const resetFeedbackDialog = () => {};
const setFeedbackState = (_: Feedback) => {};
const isFeedbackDialogOpen = false;
const UnhelpfulFeedbackContent = () => <div>Unhelpful Feedback Content</div>;
const ReportInappropriateFeedbackContent = () => <div>Report Inappropriate Feedback Content</div>;
const negativeFeedbackList: string[] = [];
const onSubmitNegativeFeedback = () => {};

// Helper for source badge (stub)
function getSourceBadge(source?: string) {
  if (!source) return null;
  return <span className={styles.sourceBadge}>{source}</span>;
}

const sourceIconMap: Record<string, { icon: string; label: string }> = {
  azure_search: { icon: AzureIcon, label: 'Azure AI Search' },
  google: { icon: GoogleIcon, label: 'Google Search' },
  general_knowledge: { icon: BookIcon, label: 'General Knowledge' }
};

export const Answer = ({ answer, source, sourceConfidence, document_metadata, onCitationClicked, onExectResultClicked, onEditDocument, simple = false, intent_reasoning, showThinkingProcess = false, isCached = false, showCacheBadge = true }: Props) => {
  // Check for generated document with workspace_id
  const generatedDocument = (answer as any).generated_document;
  const workspaceId = generatedDocument?.workspace_id;
  
  // Detect if source is from FAQ cache
  const isFromCache = isCached || Boolean(source && source.startsWith('faq_cache:'));
  
  // State for enlarged image overlay
  const [enlargedImage, setEnlargedImage] = React.useState<{src: string, alt: string} | null>(null);
  
  // Build citations list from answer
  const citations = useMemo(() => {
    const citationsArray = answer.citations || (answer as any).unified_citations || [];
    return citationsArray;
  }, [answer.citations, (answer as any).unified_citations]);
  
  // Build unified sources for the panel
  const unifiedSources: UnifiedCitationSource[] = useMemo(() => {
    if (!citations || citations.length === 0) return [];
    
    const sources: UnifiedCitationSource[] = [];
    
    citations.forEach((citation: any, index: number) => {
      // Only use actual URLs, not fallbacks to id/filename
      const rawUrl = citation.url || citation.blob_url || '';
      const url = rawUrl && (rawUrl.startsWith('/') || rawUrl.startsWith('http')) ? rawUrl : '';
      const title = citation.title || citation.content?.substring(0, 100) || 'Untitled';
      const domain = citation.domain || (url ? url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0] : 'document');
      const snippet = citation.content || citation.snippet || '';
      const sourceType = citation.type || citation.source_type || '';
      const metadata = citation.metadata || '';
      
      // Skip only special placeholder URLs, but include sources without URLs (like agentic sources)
      // Sources may not have URLs but still have valuable title/content
      const isPlaceholder = rawUrl === 'real-time-web-search' || rawUrl === 'knowledge-base';
      const hasContent = title || snippet;
      
      if (!isPlaceholder && hasContent) {
        sources.push({
          index: citation.number || index + 1,
          marker: `[${citation.number || index + 1}]`,
          title,
          url: url, // Empty string if no valid URL
          domain,
          snippet: snippet.substring(0, 200),
          type: sourceType,
          metadata: typeof metadata === 'string' ? metadata : JSON.stringify(metadata)
        });
      }
    });
    
    return sources;
  }, [citations]);
  
  // Detect RTL for locale
  const rawAnswerText = typeof answer.answer === 'string' ? answer.answer : '';
  // Clean up web citation markers like 【4:2†source】 into cleaner [4] badges
  const answerText = transformWebCitations(rawAnswerText);
  const hebrewChars = answerText.match(/[\u0590-\u05FF]/g) || [];
  const isRtl = hebrewChars.length > 20;
  const locale: 'he' | 'en' = isRtl ? 'he' : 'en';
  
  // Simple mode: render clean markdown (this is the ONLY mode that runs since forceSimple=true)
  // Note: forceSimple is kept for now but effectively this is always true
  const forceSimple = true;
  
  if (simple || forceSimple) {
    let contentText = answerText;
    
    // Handle chart data in simple mode
    let chartData = answer.generated_chart || null;
    let dashboardData = answer.generated_dashboard || null;
    
    // Check document_metadata for dashboard data if not found in answer
    if (!dashboardData && document_metadata && document_metadata.length > 0) {
      const metadataItem = document_metadata[0];
      if (metadataItem.generated_dashboard) {
        dashboardData = metadataItem.generated_dashboard;
      }
    }
    
    // Check document_metadata for chart data if not found in answer
    if (!chartData && document_metadata && document_metadata.length > 0) {
      const metadataItem = document_metadata[0];
      if (metadataItem.chart_data) {
        chartData = metadataItem.chart_data;
      }
    }
    
    // If chart data is a string, parse it
    if (typeof chartData === 'string') {
      try {
        chartData = JSON.parse(chartData);
      } catch (e) {
        console.error('Failed to parse chart JSON:', e);
        chartData = null;
      }
    }
    
    // If dashboard data is a string, parse it
    if (typeof dashboardData === 'string') {
      try {
        dashboardData = JSON.parse(dashboardData);
      } catch (e) {
        console.error('Failed to parse dashboard JSON:', e);
        dashboardData = null;
      }
    }
    
    // If we have dashboard data, render the dashboard instead of markdown
    if (dashboardData && typeof dashboardData === 'object' && dashboardData.widgets) {
      return (
        <Stack className={`${styles.answerContainer} ai-response`} tabIndex={0}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '1px', flexWrap: 'wrap' }}>
            <SourceIndicator source={source} confidence={sourceConfidence} document_metadata={document_metadata} isCached={isFromCache} showCacheBadge={showCacheBadge} />
            <ModelIndicator model={answer.model} />
            <ThinkingProcessIndicator thinking={intent_reasoning} show={showThinkingProcess} />
            {intent_reasoning?.table_routing && !intent_reasoning.table_routing.used_all_tables && (
              <span className={styles.routingBadge}>
                📊 {intent_reasoning.table_routing.tables_queried.map(t => t.split(':')[0]).join(', ')}
              </span>
            )}
          </div>
          <Dashboard dashboardData={dashboardData} />
        </Stack>
      );
    }
    
    // Chart data is now handled through document metadata, not markdown insertion
    
    // Enhanced RTL detection for mixed content with better language analysis
    const isRtlSimple = useMemo(() => {
      const text = answerText;
      
      // Count different character types for more accurate detection
      const hebrewChars = text.match(/[\u0590-\u05FF]/g) || [];
      const arabicChars = text.match(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFE70-\uFEFF]/g) || [];
      const englishChars = text.match(/[a-zA-Z]/g) || [];
      const otherLtrChars = text.match(/[À-ÿĀ-žА-я]/g) || []; // Extended Latin, Cyrillic
      
      const totalRtlChars = hebrewChars.length + arabicChars.length;
      const totalLtrChars = englishChars.length + otherLtrChars.length;
      const totalAlphabetic = totalRtlChars + totalLtrChars;
      
      // Check if text starts with RTL characters (look at first meaningful word)
      const trimmedText = text.trim();
      const startsWithRtl = /^[\s\n#*]*[\u0590-\u05FF\u0600-\u06FF]/.test(trimmedText);
      
      // Check if content has Hebrew headings or significant Hebrew blocks
      const hebrewHeadings = text.match(/#+\s*[\u0590-\u05FF][\u0590-\u05FF\s]*$/gm) || [];
      const hebrewBlocks = text.match(/[\u0590-\u05FF][\u0590-\u05FF\s.,!?:;"'()\-]{20,}/g) || [];
      
      // Calculate RTL percentage
      const rtlPercentage = totalAlphabetic > 0 ? (totalRtlChars / totalAlphabetic) * 100 : 0;
      

      
      // Enhanced direction logic for Hebrew content with balanced mixed content handling:
      let isRtl = false;
      let directionReason = '';
      
      // Special handling for calendar/meeting responses to avoid incorrect RTL alignment
      const isCalendarResponse = /(?:meeting|calendar|event|appointment|schedule|participant|attendee|organizer)/i.test(text) ||
                                 /(?:📅|🕐|👤|👥|📍|📝|⏰|🗓️)/g.test(text);
      
      if (totalAlphabetic < 10) {
        // Very short text - use LTR default
        isRtl = false;
        directionReason = 'short-text-ltr-default';
      } else if (rtlPercentage > 30) {
        // Lower threshold for majority RTL content - be more aggressive
        isRtl = true;
        directionReason = 'majority-rtl-30%';
      } else if (rtlPercentage > 20 && startsWithRtl) {
        // Even lower threshold if starts with RTL (for Office 365 content with mixed English)
        isRtl = true;
        directionReason = 'significant-rtl-starts-rtl-20%';
      } else if (rtlPercentage > 15 && startsWithRtl && hebrewChars.length > 25) {
        // Very low threshold for substantial Hebrew that starts with RTL
        isRtl = true;
        directionReason = 'substantial-hebrew-starts-rtl-15%';
      } else if (hebrewHeadings.length > 1 && rtlPercentage > 15) {
        // Hebrew headings indicate Hebrew document (very low threshold)
        isRtl = true;
        directionReason = 'hebrew-headings-detected-15%';
      } else if (hebrewBlocks.length > 2 && rtlPercentage > 15) {
        // Hebrew text blocks indicate Hebrew content (very low threshold)
        isRtl = true;
        directionReason = 'hebrew-blocks-15%';
      } else {
        // Default to LTR for mixed content, especially calendar responses with Hebrew names
        isRtl = false;
        directionReason = isCalendarResponse ? 'calendar-response-mixed-default-ltr' : 'default-ltr-or-mixed';
      }
      
      return isRtl;
    }, [answerText]);
    
    // Create reusable ReactMarkdown components config with paragraph tooltip support
    const createMarkdownComponents = (isRtl: boolean) => ({
      div: ({ node, children, ...props }: any) => (
        <div 
          {...props} 
          style={{
            ...props.style,
            direction: isRtl ? 'rtl' : 'ltr',
            textAlign: isRtl ? 'right' : 'left'
          }}
        >
          {children}
        </div>
      ),
      p: ({ node, children, ...props }: any) => {
        // Transform text children to include paragraph tooltips
        const transformedChildren = React.Children.map(children, (child) => {
          if (typeof child === 'string' && PARAGRAPH_REF_REGEX.test(child)) {
            return transformParagraphRefs(child, citations, isRtl);
          }
          return child;
        });
        
        return (
          <p 
            {...props}
            style={{
              ...props.style,
              direction: isRtl ? 'rtl' : 'ltr',
              textAlign: isRtl ? 'right' : 'left'
            }}
          >
            {transformedChildren}
          </p>
        );
      },
      // Also handle text in list items, spans, and other elements
      li: ({ node, children, ordered, ...props }: any) => {
        // Note: 'ordered' is destructured but not used - it's a ReactMarkdown prop that shouldn't be passed to DOM
        const transformedChildren = React.Children.map(children, (child) => {
          if (typeof child === 'string' && PARAGRAPH_REF_REGEX.test(child)) {
            return transformParagraphRefs(child, citations, isRtl);
          }
          return child;
        });
        return <li {...props}>{transformedChildren}</li>;
      },
      strong: ({ node, children, ...props }: any) => {
        const transformedChildren = React.Children.map(children, (child) => {
          if (typeof child === 'string' && PARAGRAPH_REF_REGEX.test(child)) {
            return transformParagraphRefs(child, citations, isRtl);
          }
          return child;
        });
        return <strong {...props}>{transformedChildren}</strong>;
      },
      em: ({ node, children, ...props }: any) => {
        const transformedChildren = React.Children.map(children, (child) => {
          if (typeof child === 'string' && PARAGRAPH_REF_REGEX.test(child)) {
            return transformParagraphRefs(child, citations, isRtl);
          }
          return child;
        });
        return <em {...props}>{transformedChildren}</em>;
      },
      code: ({ node, className, children, ...props }: any) => {
        const match = /language-(\w+)/.exec(className || '');

        // Handle code blocks with syntax highlighting
        return !match ? (
          <code className={className} {...props}>
            {children}
          </code>
        ) : (
          <SyntaxHighlighter style={nord} language={match[1]} PreTag="div">
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        );
      },
      // Open all links in new tab - convert /api/ URLs to full backend URLs
      a: ({ node, children, href, ...props }: any) => {
        // Convert relative /api/ URLs to full backend URLs for cross-origin
        const resolvedHref = href && href.startsWith('/api/') 
          ? buildApiUrl(href)
          : href;
        
        return (
          <a 
            {...props} 
            href={resolvedHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      },
      // Handle images - use ArchitectureDiagram for architecture/diagram images, simple viewer for generated images
      img: ({ node, alt, src, ...props }: any) => {
        const altLower = (alt || '').toLowerCase();
        
        // Skip if it's a chart (rendered by ChartRenderer)
        const isChart = altLower.includes('chart') || altLower.includes('generated chart');
        
        // Detect AI-generated images (from GPT Image 1.5 / DALL-E)
        const isGeneratedImage = altLower.includes('generated image') || 
                                  altLower.includes('generated') ||
                                  altLower.includes('תמונה שנוצרה');
        
        // Detect architecture diagrams by:
        // 1. Alt text contains architecture/diagram keywords
        // 2. Base64 PNG images that are NOT generated images
        const isDiagramByAlt = altLower.includes('architecture') || 
                               altLower.includes('diagram') ||
                               altLower.includes('ארכיטקטורה');
        
        // Base64 PNG images that aren't charts or generated images are likely diagrams
        const isBase64Png = src && src.startsWith('data:image/png;base64,');
        
        const isDiagram = !isChart && !isGeneratedImage && (isDiagramByAlt || isBase64Png);
        
        // Use ArchitectureDiagram only for actual diagrams
        if (isDiagram && src) {
          return (
            <ArchitectureDiagram 
              src={src}
              alt={alt || 'Architecture Diagram'}
              title={alt || 'Architecture Diagram'}
            />
          );
        }
        
        // Generated images get a simple thumbnail with download/enlarge icons
        if (isGeneratedImage && src) {
          const handleDownload = (e: React.MouseEvent) => {
            e.stopPropagation();
            // Convert base64 to blob and download
            if (src.startsWith('data:')) {
              // Detect file extension from mime type in data URI
              const isJpeg = src.includes('image/jpeg') || src.includes('image/jpg');
              const ext = isJpeg ? '.jpg' : '.png';
              
              const link = document.createElement('a');
              link.href = src;
              link.download = (alt || 'generated-image').replace(/[^a-zA-Z0-9]/g, '_') + ext;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } else {
              window.open(src, '_blank');
            }
          };
          
          const handleEnlarge = (e: React.MouseEvent) => {
            e.stopPropagation();
            setEnlargedImage({ src, alt: alt || 'Generated Image' });
          };
          
          return (
            <div style={{ 
              display: 'inline-block', 
              position: 'relative',
              margin: '8px 4px',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              maxWidth: '300px'
            }}
            className="generated-image-container"
            >
              <img 
                src={src}
                alt={alt}
                loading="eager"
                decoding="async"
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  display: 'block',
                  cursor: 'pointer',
                  willChange: 'transform'
                }}
                onClick={handleEnlarge}
                title="Click to enlarge"
              />
              {/* Action buttons overlay */}
              <div style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                display: 'flex',
                gap: '6px'
              }}>
                <button
                  onClick={handleEnlarge}
                  title="Enlarge image"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.6)'}
                >
                  <ArrowExpand20Regular />
                </button>
                <button
                  onClick={handleDownload}
                  title="Download image"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.6)'}
                >
                  <ArrowDownload20Regular />
                </button>
              </div>
            </div>
          );
        }
        
        // Regular image
        return (
          <img 
            {...props}
            alt={alt}
            src={src}
          />
        );
      }
    });

    // Memoize the components config
    const markdownComponents = useMemo(() => createMarkdownComponents(isRtlSimple), [isRtlSimple, citations]);
    
    return (
      <Stack 
        className={`${styles.answerContainer} ai-response`} 
        tabIndex={0}
        style={{ 
          direction: isRtlSimple ? 'rtl' : 'ltr', 
          textAlign: isRtlSimple ? 'right' : 'left' 
        }}
        dir={isRtlSimple ? 'rtl' : 'ltr'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '1px', flexWrap: 'wrap' }}>
          <SourceIndicator source={source} confidence={sourceConfidence} document_metadata={document_metadata} isCached={isFromCache} showCacheBadge={showCacheBadge} />
          <ModelIndicator model={answer.model} />
          <ThinkingProcessIndicator thinking={intent_reasoning} show={showThinkingProcess} />
          {intent_reasoning?.table_routing && !intent_reasoning.table_routing.used_all_tables && (
            <span className={styles.routingBadge}>
              📊 {intent_reasoning.table_routing.tables_queried.map(t => t.split(':')[0]).join(', ')}
            </span>
          )}
        </div>
        
        <div style={{
          direction: isRtlSimple ? 'rtl' : 'ltr',
          textAlign: isRtlSimple ? 'right' : 'left',
          width: '100%'
        }}>
          {(() => {
            // Split content to insert chart before Business Insights section (Hebrew or English)
            const businessInsightsMarkerEn = '### 💡 Business Insights';
            const businessInsightsMarkerHe = '### 💡 תובנות עסקיות';
            const keyInsightsMarker = '### 💡 Key Insights';
            
            // Get chart data from answer.generated_chart first, then fall back to document_metadata
            let chartDataLocal = answer.generated_chart || null;
            let chartDataArray: any[] = [];  // Support multiple charts
            
            if (!chartDataLocal && document_metadata && document_metadata.length > 0) {
              // Check for multiple charts (chart_specs array) first
              if (document_metadata[0].chart_specs && Array.isArray(document_metadata[0].chart_specs)) {
                chartDataArray = document_metadata[0].chart_specs;
                chartDataLocal = chartDataArray[0] || null;  // Keep first chart for backward compat
              } else {
                chartDataLocal = document_metadata[0].chart_data || null;
              }
            }
            
            // Parse chart data if it's a string
            if (typeof chartDataLocal === 'string') {
              try {
                chartDataLocal = JSON.parse(chartDataLocal);
              } catch (e) {
                console.error('Failed to parse chart JSON:', e);
                chartDataLocal = null;
              }
            }
            
            // If we have a single chart but not an array, wrap it
            if (chartDataLocal && chartDataArray.length === 0) {
              chartDataArray = [chartDataLocal];
            }
            
            // Check for either Hebrew or English Business Insights marker
            const hasEnglishMarker = answerText.includes(businessInsightsMarkerEn);
            const hasHebrewMarker = answerText.includes(businessInsightsMarkerHe);
            const hasKeyInsightsMarker = answerText.includes(keyInsightsMarker);
            
            // Determine which marker to use for splitting
            let insightsMarker = '';
            if (hasEnglishMarker) insightsMarker = businessInsightsMarkerEn;
            else if (hasHebrewMarker) insightsMarker = businessInsightsMarkerHe;
            else if (hasKeyInsightsMarker) insightsMarker = keyInsightsMarker;
            
            const hasAnyInsightsMarker = hasEnglishMarker || hasHebrewMarker || hasKeyInsightsMarker;
            
            // Render all charts
            const renderCharts = () => {
              if (chartDataArray.length === 0) return null;
              return (
                <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                  {chartDataArray.map((chartData, index) => (
                    <div key={index} style={{ marginBottom: index < chartDataArray.length - 1 ? '30px' : '0' }}>
                      <ChartRenderer chartData={chartData} />
                    </div>
                  ))}
                </div>
              );
            };
            
            if (chartDataArray.length > 0 && hasAnyInsightsMarker) {
              const parts = answerText.split(insightsMarker);
              return (
                <>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, supersub]}
                    rehypePlugins={[rehypeRaw]}
                    className={`ai-response-content ${isRtlSimple ? 'rtl-content' : 'ltr-content'}`}
                    components={markdownComponents}
                  >
                    {parts[0]}
                  </ReactMarkdown>
                  
                  {/* Render all charts before Business Insights */}
                  {renderCharts()}
                  
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, supersub]}
                    rehypePlugins={[rehypeRaw]}
                    className={`ai-response-content ${isRtlSimple ? 'rtl-content' : 'ltr-content'}`}
                    components={markdownComponents}
                  >
                    {insightsMarker + parts[1]}
                  </ReactMarkdown>
                </>
              );
            } else if (chartDataArray.length > 0) {
              // Chart data exists but no Business Insights markers - render charts at the end
              return (
                <>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, supersub]}
                    rehypePlugins={[rehypeRaw]}
                    className={`ai-response-content ${isRtlSimple ? 'rtl-content' : 'ltr-content'}`}
                    components={markdownComponents}
                  >
                    {answerText}
                  </ReactMarkdown>
                  
                  {/* Render all charts at the end */}
                  {renderCharts()}
                </>
              );
            } else {
              // No Business Insights section or no chart data - render normally
              return (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, supersub]}
                  rehypePlugins={[rehypeRaw]}
                  className={`ai-response-content ${isRtlSimple ? 'rtl-content' : 'ltr-content'}`}
                  components={markdownComponents}
                >
                  {answerText}
                </ReactMarkdown>
              );
            }
          })()}
        </div>
        
        {/* Re-open Document Panel button - only shows when panel was closed */}
        {workspaceId && onEditDocument && (
          <div style={{ marginTop: '8px', marginBottom: '4px' }}>
            <DefaultButton
              iconProps={{ iconName: 'DocumentEdit' }}
              text="Open Document Panel"
              onClick={() => onEditDocument(workspaceId)}
              styles={{
                root: {
                  borderRadius: '6px',
                  fontSize: '12px',
                  height: '28px',
                  padding: '0 10px',
                },
              }}
            />
          </div>
        )}
        
        {/* View/Download Generated Document buttons - shows for documents with URL */}
        {generatedDocument?.document_url && generatedDocument?.filename && !workspaceId && (
          <div style={{ marginTop: '8px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* View - opens in Office 365 for Office files, inline for PDFs */}
            <DefaultButton
              iconProps={{ iconName: 'View' }}
              text={locale === 'he' ? 'צפייה' : 'View'}
              onClick={() => openFile(generatedDocument.document_url, generatedDocument.filename)}
              styles={{
                root: {
                  borderRadius: '6px',
                  fontSize: '12px',
                  height: '28px',
                  padding: '0 10px',
                  backgroundColor: '#0078d4',
                  borderColor: '#0078d4',
                  color: 'white',
                },
                rootHovered: {
                  backgroundColor: '#106ebe',
                  borderColor: '#106ebe',
                },
                icon: {
                  color: 'white',
                }
              }}
            />
            {/* Download - always downloads */}
            <DefaultButton
              iconProps={{ iconName: 'Download' }}
              text={locale === 'he' ? 'הורדה' : 'Download'}
              onClick={() => downloadFile(generatedDocument.document_url, generatedDocument.filename)}
              styles={{
                root: {
                  borderRadius: '6px',
                  fontSize: '12px',
                  height: '28px',
                  padding: '0 10px',
                  backgroundColor: '#667eea',
                  borderColor: '#667eea',
                  color: 'white',
                },
                rootHovered: {
                  backgroundColor: '#764ba2',
                  borderColor: '#764ba2',
                },
                icon: {
                  color: 'white',
                }
              }}
            />
            <span style={{ fontSize: '11px', color: '#666' }}>
              {generatedDocument.filename} ({generatedDocument.format?.toUpperCase()})
            </span>
          </div>
        )}
        
        {/* Sources tiles - compact inline display */}
        {unifiedSources.length > 0 && (
          <CitationSourcesPanel
            sources={unifiedSources}
            locale={locale}
          />
        )}
        
        {/* Image Enlargement Overlay */}
        {enlargedImage && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.9)',
              zIndex: 10000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={() => setEnlargedImage(null)}
          >
            {/* Close button */}
            <button
              onClick={() => setEnlargedImage(null)}
              title="Close"
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            >
              <Dismiss24Regular />
            </button>
            
            {/* Image */}
            <img 
              src={enlargedImage.src}
              alt={enlargedImage.alt}
              style={{
                maxWidth: '90vw',
                maxHeight: '85vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 32px rgba(0,0,0,0.5)'
              }}
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Caption and download */}
            <div style={{
              marginTop: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              color: 'white'
            }}>
              <span style={{ fontSize: '14px', opacity: 0.8 }}>{enlargedImage.alt}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (enlargedImage.src.startsWith('data:')) {
                    // Detect file extension from mime type in data URI
                    const isJpeg = enlargedImage.src.includes('image/jpeg') || enlargedImage.src.includes('image/jpg');
                    const ext = isJpeg ? '.jpg' : '.png';
                    
                    const link = document.createElement('a');
                    link.href = enlargedImage.src;
                    link.download = (enlargedImage.alt || 'image').replace(/[^a-zA-Z0-9]/g, '_') + ext;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                }}
                title="Download image"
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
              >
                <ArrowDownload20Regular /> Download
              </button>
            </div>
          </div>
        )}
      </Stack>
    );
  }
  
  // This return is never reached since forceSimple is always true
  // but TypeScript needs it for the function to be complete
  return null;
};
